import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FairBadge, RevealWaiting } from './Fairness.tsx';
import { SHOW_FAIRNESS, recordObserved } from '../lib/fairness.ts';
import { DevActPanel, useDevTools } from './DevActPanel.tsx';
import { useShowdown } from './Showdown.ts';
import { playClockTick, playTurnTick, playYourTurn, useSoundSetting } from '../lib/sound.ts';
import { TICK_SECONDS } from '../../shared/turns.ts';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t, chips, duration, dateTime, errorText, smartTime, tDynamic } from '../i18n/index.ts';
import { api, ApiError, newKey } from '../lib/api.ts';
import { useMaintenance, useSession } from '../lib/session.tsx';
import { useFinishRevealed } from '../lib/finishReveal.ts';
import { Avatar, Dialog, EmptyCard, ErrorView, InlineCards, Loading, PlayingCard, Status, useNow, useToast } from '../components/ui.tsx';
import type { TableView, SeatView, HandSummary } from '../../server/domain/game.ts';
import type { HandEvent, LegalActions } from '../../server/engine/hand.ts';
import type { HandView } from '../../server/engine/view.ts';
import type { Card } from '../../shared/cards.ts';
import { ChannelView } from './Chat.tsx';
import { TabBar, UnitToggle } from '../components/TabBar.tsx';
import { TurnNotifyControl } from '../components/TurnNotify.tsx';
import { Icon } from '../components/ui.tsx';
import { motion, AnimatePresence } from 'motion/react';
import { makeFormatter, useAmountUnit, type AmountFormatter, type AmountUnit } from '../lib/amounts.ts';

type SinceSummary = {
  since: number;
  handsCompleted: number;
  chipDelta: number;
  biggestPots: { handNo: number; pot: number; winners: string[] }[];
  events: { kind: string; data: Record<string, unknown>; handNo: number | null; createdAt: number }[];
  myTimeouts: number;
} | null;

// ---------------------------------------------------------------------------
// Text helpers

/** 0.634 → "63 %"; never "0 %" or "100 %" while a card can still change it. */
function equityText(share: number): string {
  const p = share * 100;
  const shown = p > 0 && p < 1 ? '<1' : p < 100 && p > 99 ? '>99' : String(Math.round(p));
  return `${shown} %`;
}

export function describeEvent(e: HandEvent, name: (seat: number) => string, fmt: AmountFormatter = chips): ReactNode {
  switch (e.t) {
    case 'ante':
      return t('log.ante', { name: name(e.seat), amount: fmt(e.amount) }) + (e.allIn ? t('log.allin') : '');
    case 'blind':
      return t(e.kind === 'sb' ? 'log.sb' : 'log.bb', { name: name(e.seat), amount: fmt(e.amount) }) + (e.allIn ? t('log.allin') : '');
    case 'deal':
      return t('log.deal');
    case 'action': {
      const amount = fmt(e.action === 'call' ? e.added : e.to);
      const text = t(`log.${e.action}`, { name: name(e.seat), amount });
      const suffix = (e.allIn ? t('log.allin') : '') + (e.auto === 'timeout' ? t('log.autoTimeout') : e.auto === 'away' ? t('log.autoAway') : '');
      return (
        <span className={e.auto ? 'auto' : undefined}>
          {text}
          {suffix}
        </span>
      );
    }
    case 'return':
      return t('log.return', { name: name(e.seat), amount: fmt(e.amount) });
    case 'street':
      return (
        <>
          {t(`table.streets.${e.street}`)}: <InlineCards cards={e.cards} />
        </>
      );
    case 'reveal':
      return (
        <>
          {t('log.reveal', { name: name(e.seat), cards: '' })}
          <InlineCards cards={e.cards} />
        </>
      );
    case 'muck':
      return t('log.muck', { name: name(e.seat) });
    case 'award':
      return t('log.award', { name: name(e.seat), amount: fmt(e.amount) });
  }
}

export function describeTournamentEvent(e: { kind: string; data: Record<string, unknown> }, userName: (id: string) => string): string {
  const d = e.data;
  const place = d.placeFrom === d.placeTo ? `${d.placeFrom}.` : `${d.placeFrom}.–${d.placeTo}.`;
  return tDynamic(
    `events.${e.kind}`,
    {
      name: typeof d.userId === 'string' ? userName(d.userId) : '',
      reason: String(d.reason ?? ''),
      sb: chips(Number(d.sb ?? 0)),
      bb: chips(Number(d.bb ?? 0)),
      version: String(d.version ?? ''),
      place,
    },
    e.kind,
  );
}

// ---------------------------------------------------------------------------
// Pieces

function DeadlineRing({ user, fraction, active }: { user: SeatView['user']; fraction: number | null; active: boolean }) {
  const r = 24;
  const c = 2 * Math.PI * r;
  return (
    <span className="ring">
      <Avatar name={user.displayName} url={user.avatarUrl} />
      {active && fraction !== null && (
        <svg viewBox="0 0 52 52" aria-hidden="true">
          <circle className="track" cx="26" cy="26" r={r} strokeWidth="3" />
          <circle className={`prog ${fraction < 0.15 ? 'low' : ''}`} cx="26" cy="26" r={r} strokeWidth="3.5" strokeDasharray={c} strokeDashoffset={c * (1 - fraction)} />
        </svg>
      )}
    </span>
  );
}

interface SeatModel {
  seat: SeatView;
  inHand: HandView['players'][number] | null;
  acting: boolean;
  isMe: boolean;
  isButton: boolean;
  isSb: boolean;
  isBb: boolean;
}

function SeatTags({ m }: { m: SeatModel }) {
  return (
    <span className="seat-tags">
      {m.isButton && (
        <span className="chip-tag dealer" title={t('table.dealer')}>
          <span aria-hidden="true">D</span>
          <span className="visually-hidden">{t('table.dealer')}</span>
        </span>
      )}
      {m.isSb && <span className="chip-tag">{t('table.sbShort')}</span>}
      {m.isBb && <span className="chip-tag">{t('table.bbShort')}</span>}
      {m.inHand?.allIn && <span className="chip-tag allin">{t('table.allIn')}</span>}
      {m.inHand?.folded && <span className="chip-tag">{t('table.folded')}</span>}
      {m.seat.away && m.seat.status === 'playing' && <span className="chip-tag">{t('table.away')}</span>}
      {m.seat.status === 'eliminated' && (
        <span className="chip-tag">
          {t('table.out')} {m.seat.placeFrom === m.seat.placeTo ? `${m.seat.placeFrom}.` : `${m.seat.placeFrom}.–${m.seat.placeTo}.`}
        </span>
      )}
      {m.acting && <span className="visually-hidden">{t('table.toAct')}</span>}
    </span>
  );
}

function SeatCards({ m, size, best }: { m: SeatModel; size: 's' | 'm'; best?: Set<Card> | null }) {
  if (!m.inHand || m.inHand.folded) return null;
  const cards = m.inHand.hole;
  return (
    <span className={`cards cards-tight ${cards ? 'shown' : ''}`}>
      {cards
        ? cards.map((c, i) => (
            // Shown cards turn over (showdown, all-in).
            <motion.span key={c} className="card-motion" initial={{ rotateY: 180, opacity: 0.4 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.5, delay: i * 0.12 }}>
              <PlayingCard card={c} size={size} highlight={!!best?.has(c)} dim={!!best && !best.has(c)} />
            </motion.span>
          ))
        : [0, 1].map((i) => <PlayingCard key={i} card={null} size={size} />)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Action dock

interface Preset {
  label: string;
  to: number;
  disabled: boolean;
}

/** Pot-fraction presets. Sizes below the minimum raise stay visible but disabled. */
function sizePresets(legal: LegalActions, hand: HandView): Preset[] {
  const range = legal.bet ?? legal.raise;
  if (!range) return [];
  const toCall = legal.call?.amount ?? 0;
  const potAfterCall = hand.totalPot + toCall;
  const fraction = (f: number, label: string): Preset => {
    const to = Math.round(hand.currentBet + potAfterCall * f);
    return { label, to: Math.min(to, range.max), disabled: to < range.min || to >= range.max };
  };
  return [
    fraction(0.25, '25 %'),
    fraction(0.5, '50 %'),
    fraction(0.75, '75 %'),
    fraction(1, t('table.presetPot')),
    { label: t('table.allin'), to: range.max, disabled: false },
  ];
}

function ActionDock({
  data,
  onActed,
  unit,
  fmt,
  variant = 'dock',
}: {
  data: TableView;
  onActed: () => void;
  unit: AmountUnit;
  fmt: AmountFormatter;
  /** 'bar': phones — one row of buttons at the bottom; the bet size opens above it. */
  variant?: 'dock' | 'bar';
}) {
  const toast = useToast();
  const { id } = useParams();
  const my = data.myTurn!;
  const hand = data.hand!;
  const legal = my.legal;
  const range = legal.bet ?? legal.raise;
  const bb = hand.level.bb;
  const [amount, setAmount] = useState<number>(range?.min ?? 0);
  // The text box edits the amount in the chosen unit (chips, or big blinds with one decimal).
  const toText = (chipsValue: number) => (unit === 'bb' ? String(Math.round((chipsValue / bb) * 10) / 10).replace('.', ',') : String(chipsValue));
  const [text, setText] = useState(toText(range?.min ?? 0));
  const [confirmAllIn, setConfirmAllIn] = useState(false);
  const [sizing, setSizing] = useState(false);
  const keys = useRef(new Map<string, string>());
  useEffect(() => {
    setAmount(range?.min ?? 0);
    setText(toText(range?.min ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [my.turnId, range?.min, unit]);

  const act = useMutation({
    mutationFn: (action: { type: string; amount?: number }) => {
      // The same intent on the same turn reuses its idempotency key, so a retry after a
      // lost response can never be applied twice.
      const intent = `${my.turnId}:${action.type}:${action.amount ?? ''}`;
      if (!keys.current.has(intent)) keys.current.set(intent, newKey());
      return api.post(`/api/tournaments/${id}/actions`, { turnId: my.turnId, ...action, idempotencyKey: keys.current.get(intent) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'turn_stale') toast(t('table.staleTurn'), 'error');
      else if (err instanceof ApiError && err.code === 'deadline_passed') toast(t('table.deadlinePassed'), 'error');
      else toast(errorText(err), 'error');
    },
    onSettled: onActed,
  });
  const busy = act.isPending;
  const valid = range ? Number.isInteger(amount) && amount >= range.min && amount <= range.max : false;
  const sizeType = legal.bet ? 'bet' : 'raise';
  const isAllIn = !!range && amount === range.max;
  const setBoth = (v: number) => {
    const clamped = range ? Math.max(range.min, Math.min(range.max, v)) : v;
    setAmount(clamped);
    setText(toText(clamped));
  };
  const step = Math.max(1, bb);
  const raiseLabel = isAllIn ? t('table.allin') : t(sizeType === 'bet' ? 'table.bet' : 'table.raise');
  const callAllIn = !!legal.call?.allIn;
  const sizeName = sizeType === 'bet' ? t('table.betTo') : t('table.raiseTo');

  const bar = variant === 'bar';
  const sizer = range && (
        <div className="sizer">
          <div className="seg" role="group" aria-label={t('table.sizeLabel')}>
            {sizePresets(legal, hand).map((p) => (
              <button key={p.label} type="button" disabled={p.disabled || busy} aria-pressed={!p.disabled && amount === p.to} onClick={() => setBoth(p.to)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="sizer-row">
            <div className="stepper">
              <button type="button" aria-label={t('table.decrease')} disabled={busy || amount <= range.min} onClick={() => setBoth(amount - step)}>
                −
              </button>
              <input
                type="range"
                min={range.min}
                max={range.max}
                step={1}
                value={Math.min(Math.max(amount, range.min), range.max)}
                aria-label={sizeName}
                aria-valuetext={fmt(amount)}
                onChange={(e) => setBoth(Number(e.target.value))}
              />
              <button type="button" aria-label={t('table.increase')} disabled={busy || amount >= range.max} onClick={() => setBoth(amount + step)}>
                +
              </button>
            </div>
            <label className="amount-box">
              <span className="visually-hidden">{sizeName}</span>
              <input
                id="amount"
                inputMode="numeric"
                value={text}
                aria-describedby="amount-help"
                aria-invalid={!valid}
                onChange={(e) => {
                  setText(e.target.value);
                  const n = Number(e.target.value.replace(/\s/g, '').replace(',', '.'));
                  const value = unit === 'bb' ? Math.round(n * bb) : Math.floor(n);
                  setAmount(Number.isFinite(value) ? value : 0);
                }}
                onBlur={() => {
                  if (!valid) setBoth(amount);
                }}
              />
              <span className="amount-unit">{unit === 'bb' ? 'BB' : t('table.chipsUnit')}</span>
            </label>
          </div>
          <span id="amount-help" className="hint">
            {sizeName}: {fmt(amount)}. {t('table.adds', { amount: fmt(Math.max(0, amount - legal.streetBet)) })}. {t('table.minMax', { min: fmt(range.min), max: fmt(range.max) })}
          </span>
        </div>
  );

  return (
    <div className={bar ? 'abar' : undefined}>
      {bar ? (
        <AnimatePresence>
          {sizing && sizer && (
            <motion.div
              className="abar-sheet"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12, transition: { duration: 0.12 } }}
              transition={{ type: 'spring', stiffness: 460, damping: 34 }}
            >
              <div className="abar-sheet-head">
                <strong>{sizeName}</strong>
                <button type="button" className="install-close" aria-label={t('app.close')} onClick={() => setSizing(false)}>
                  <Icon name="close" />
                </button>
              </div>
              {sizer}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        sizer
      )}
      <div className="actions" role="group" aria-label={t('table.yourTurn')}>
        {legal.canFold && (
          <button className="act act-fold" disabled={busy} onClick={() => act.mutate({ type: 'fold' })}>
            <span className="act-label">{t('table.fold')}</span>
          </button>
        )}
        {legal.canCheck && (
          <button className="act act-call" disabled={busy} onClick={() => act.mutate({ type: 'check' })}>
            <span className="act-label">{t('table.check')}</span>
          </button>
        )}
        {legal.call && (
          <button
            className="act act-call"
            disabled={busy}
            aria-label={`${callAllIn ? t('table.callAllInShort') : t('table.callShort')} ${fmt(legal.call.amount)}`}
            onClick={() => (callAllIn ? setConfirmAllIn(true) : act.mutate({ type: 'call' }))}
          >
            <span className="act-label">{callAllIn ? t('table.callAllInShort') : t('table.callShort')}</span>
            <span className="act-amount">{fmt(legal.call.amount)}</span>
          </button>
        )}
        {range &&
          (bar && !sizing ? (
            <button className="act act-raise" disabled={busy} aria-expanded={false} onClick={() => setSizing(true)}>
              <span className="act-label">{t(sizeType === 'bet' ? 'table.bet' : 'table.raise')}</span>
              <span className="act-amount" aria-hidden="true">
                ▲
              </span>
            </button>
          ) : (
            <button
              className="act act-raise"
              disabled={busy || !valid}
              aria-label={`${raiseLabel} ${fmt(amount)}`}
              onClick={() => (isAllIn ? setConfirmAllIn(true) : act.mutate({ type: sizeType, amount }))}
            >
              <span className="act-label">{raiseLabel}</span>
              <span className="act-amount">{fmt(amount)}</span>
            </button>
          ))}
      </div>
      {confirmAllIn && (
        <Dialog title={t('table.confirmAction', { action: t('table.allin') })} onClose={() => setConfirmAllIn(false)}>
          <p>{t('table.adds', { amount: fmt(callAllIn && !isAllIn ? legal.call!.amount : Math.max(0, (range?.max ?? 0) - legal.streetBet)) })}</p>
          <div className="btn-row">
            <button
              className="btn btn-brass"
              disabled={busy}
              onClick={() => {
                setConfirmAllIn(false);
                if (callAllIn && !isAllIn) act.mutate({ type: 'call' });
                else act.mutate({ type: sizeType, amount: range!.max });
              }}
            >
              {t('table.allin')}
            </button>
            <button className="btn" data-close onClick={() => setConfirmAllIn(false)}>
              {t('app.cancel')}
            </button>
          </div>
        </Dialog>
      )}
      {busy && !bar && (
        <p className="hint" role="status">
          {t('table.sending')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page

export function TablePage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { me } = useSession();
  const maintenance = useMaintenance();
  const finishRevealed = useFinishRevealed(id);
  const toast = useToast();
  const q = useQuery({ queryKey: ['table', id], queryFn: () => api.get<TableView>(`/api/tournaments/${id}/table`), refetchInterval: 30_000 });
  const since = useQuery({ queryKey: ['since', id], queryFn: () => api.get<{ summary: SinceSummary }>(`/api/tournaments/${id}/since-last-visit`), staleTime: Infinity, refetchOnWindowFocus: false });
  const [sinceDismissed, setSinceDismissed] = useState(false);
  // Side panel: chat first (when available); the player can hide the whole panel.
  const [side, setSide] = useState<'log' | 'chat' | null>(null);
  const [panelPref, setPanelPref] = usePersistentFlag('kotipoker.tablePanel', true);
  const [immersive, setImmersive] = useImmersive();
  // On phones the fullscreen table starts without the panel; it slides over the table when opened.
  // Phones, portrait tablets and short landscape screens: bottom action bar and compact table.
  const smallScreen = useMediaQuery('(max-width: 900px), (max-height: 520px)');
  const [overlayPanel, setOverlayPanel] = useState(false);
  const devTools = useDevTools();
  const overlay = immersive && smallScreen;
  const panelOpen = overlay ? overlayPanel : panelPref;
  const setPanelOpen = (open: boolean) => (overlay ? setOverlayPanel(open) : void setPanelPref(open));
  const [sideWidth, setSideWidth] = usePanelWidth();
  const seen = useMutation({ mutationFn: () => api.post(`/api/tournaments/${id}/seen`) });
  const back = useMutation({
    mutationFn: () => api.post(`/api/tournaments/${id}/back`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['table', id] }),
    onError: (e) => toast(errorText(e), 'error'),
  });

  // Mark as seen when leaving the page (and right away on the first ever visit).
  useEffect(() => {
    if (since.data && since.data.summary === null) seen.mutate();
    return () => {
      void api.post(`/api/tournaments/${id}/seen`).catch(() => undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [since.data, id]);

  // Server clock minus this device's clock, measured when the response arrived.
  const offset = q.data ? q.data.serverTime - q.dataUpdatedAt : 0;
  const now = useNow(offset, 1000);
  const data = q.data;

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of data?.seats ?? []) map.set(s.user.id, s.user.displayName);
    return (userId: string) => map.get(userId) ?? '?';
  }, [data?.seats]);

  const [unit, setUnit] = useAmountUnit();
  const channel = useChannelId(id!, !!data && data.tournament.chatEnabled && (data.me !== null || me?.role === 'superadmin'));
  // Remember what this device saw (own cards, board) so the hand verifier can compare later.
  useEffect(() => {
    const h = data?.hand;
    const mine = h?.players.find((p) => p.userId === me?.id);
    if (h && me && mine?.hole && data?.fairness) recordObserved(data.tournament.id, me.id, h.handNo, { seat: mine.seat, hole: mine.hole, board: h.board });
  }, [data, me]);

  // A small chime when the turn moves while the page is open (a clearer one for your own turn).
  const [soundsOn] = useSoundSetting();
  const lastTurn = useRef<string | null | undefined>(undefined);
  const turnKey = data?.turn ? `${data.turn.userId}:${data.turn.deadlineAt ?? data.turn.pausedRemainingMs}` : null;
  useEffect(() => {
    if (lastTurn.current !== undefined && turnKey && turnKey !== lastTurn.current && soundsOn) {
      if (data?.turn?.userId === me?.id) playYourTurn();
      else playTurnTick();
    }
    lastTurn.current = turnKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnKey]);

  // The clock ticks audibly in the last seconds of your own turn.
  const myDeadline = data?.turn && data.turn.userId === me?.id ? data.turn.deadlineAt : null;
  const secondsLeft = myDeadline != null ? Math.ceil((myDeadline - now) / 1000) : null;
  useEffect(() => {
    if (soundsOn && secondsLeft !== null && secondsLeft > 0 && secondsLeft <= TICK_SECONDS) playClockTick(secondsLeft <= 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  // When a hand this page watched ends, its showdown or runout is played out on the felt first.
  const show = useShowdown({
    tournamentId: data?.tournament.id,
    liveHandNo: data?.hand?.handNo ?? null,
    liveBoard: data?.hand?.board.length ?? 0,
    previousHandNo: data?.previousHand?.handNo ?? null,
    viewerId: me?.id,
  });

  if (q.isPending) return <div className="page" style={{ padding: 24 }}><Loading /></div>;
  if (q.isError) return <div className="page" style={{ padding: 24 }}><ErrorView error={q.error} onRetry={() => q.refetch()} /></div>;
  const d = data!;
  const spoiler = d.tournament.status === 'finished' && !finishRevealed;
  const hand = show.active ? show.hand : d.hand;
  const seatBySeatNo = new Map(d.seats.map((s) => [s.seat, s]));
  const seatName = (seat: number) => seatBySeatNo.get(seat)?.user.displayName ?? `#${seat}`;
  const mySeat = d.me?.seat ?? null;
  const models: SeatModel[] = d.seats.map((s) => {
    const inHand = hand?.players.find((p) => p.seat === s.seat) ?? null;
    return {
      seat: s,
      inHand,
      acting: !show.active && d.turn?.seat === s.seat,
      isMe: s.user.id === me?.id,
      isButton: hand?.buttonSeat === s.seat,
      isSb: !!hand && hand.sbSeat === s.seat && hand.sbSeat !== hand.buttonSeat,
      isBb: hand?.bbSeat === s.seat,
    };
  });
  // Rotate so the viewer sits at the bottom.
  const startIdx = mySeat !== null ? models.findIndex((m) => m.seat.seat === mySeat) : 0;
  const ordered = [...models.slice(Math.max(0, startIdx)), ...models.slice(0, Math.max(0, startIdx))];
  const turnFraction =
    d.turn?.deadlineAt != null ? Math.max(0, Math.min(1, (d.turn.deadlineAt - now) / (d.tournament.turnSeconds * 1000))) : d.turn?.pausedRemainingMs != null ? d.turn.pausedRemainingMs / (d.tournament.turnSeconds * 1000) : null;
  const actingName = d.turn ? nameOf(d.turn.userId) : null;
  const fmt = makeFormatter(unit, hand?.level.bb ?? d.tournament.level.bb);
  const myPlayer = d.hand?.players.find((p) => p.userId === me?.id) ?? null;
  const lastAction = hand ? [...hand.log].reverse().find((e) => e.t === 'action') : undefined;
  const level = d.tournament.level;
  const summary = since.data?.summary ?? null;
  const showSince = !sinceDismissed && summary && (summary.handsCompleted > 0 || summary.events.length > 0);
  const chatAvailable = d.tournament.chatEnabled && (d.me !== null || me?.role === 'superadmin');
  const activeSide: 'log' | 'chat' = side ?? (chatAvailable ? 'chat' : 'log');

  const compactUi = smallScreen;
  // Pre-selections: in the hand, still able to act, and someone else is deciding now.
  const canPreSelect = !!hand && !!myPlayer && !myPlayer.folded && !myPlayer.allIn && !d.myTurn && !!d.turn && d.tournament.status === 'running';
  const statusPill =
    compactUi && d.turn ? (
      <div className={`table-status ${d.myTurn ? 'mine' : ''}`}>
        <span className="table-status-dot" aria-hidden="true" />
        {/* Only who is acting is announced; the ticking time is not. */}
        <span className="table-status-who" aria-live="polite">{d.myTurn ? t('table.yourTurn') : t('table.waitingFor', { name: actingName ?? '' })}</span>
        <span className="table-status-time num">
          {d.turn.deadlineAt !== null ? t('table.timeLeft', { time: duration(d.turn.deadlineAt - now) }) : t('table.pausedShort')}
        </span>
      </div>
    ) : null;

  const deadlineBlock = d.turn ? (
    <div className="deadline">
      {d.turn.deadlineAt !== null ? (
        <>
          <strong>{t('table.timeLeft', { time: duration(d.turn.deadlineAt - now) })}</strong>
          <span>{t('table.deadline', { time: dateTime(d.turn.deadlineAt) })}</span>
        </>
      ) : (
        <strong>{t('table.pausedLeft', { time: duration(d.turn.pausedRemainingMs ?? 0) })}</strong>
      )}
    </div>
  ) : null;

  const potBlock = hand && (
    <>
      <div className="pot num">
        {t('table.pot')} {fmt(show.active ? show.potTotal : hand.totalPot)}
        {!show.active && hand.pots.length > 1 && (
          <small>
            {hand.pots.map((p, i) => `${i === 0 ? t('table.mainPot') : t('table.sidePot', { n: i })} ${fmt(p.amount)}`).join(', ')}
          </small>
        )}
      </div>
      <div className="board" role="group" aria-label={t('table.board')}>
        {[0, 1, 2, 3, 4].map((i) =>
          hand.board[i] ? (
            <motion.span
              key={`${hand.handNo}-${hand.board[i]}`}
              className={`card-motion ${show.dramaticIndex === i ? 'dramatic' : ''}`}
              initial={show.dramaticIndex === i ? { rotateY: 180, scale: 1.35, y: -24, opacity: 0 } : { rotateY: 90, y: -10, opacity: 0 }}
              animate={{ rotateY: 0, scale: 1, y: 0, opacity: 1 }}
              transition={
                show.dramaticIndex === i
                  ? { duration: 1.1, ease: [0.16, 1, 0.3, 1] }
                  : { type: 'spring', stiffness: 260, damping: 24, delay: (i < 3 ? i : 0) * (show.active ? 0.22 : 0.08) }
              }
            >
              <PlayingCard
                card={hand.board[i] as Card}
                size="m"
                highlight={!!show.bestCards?.has(hand.board[i] as Card)}
                dim={!!show.bestCards && !show.bestCards.has(hand.board[i] as Card)}
              />
            </motion.span>
          ) : (
            <EmptyCard key={i} size="m" />
          ),
        )}
      </div>
    </>
  );
  const lastActionLine = lastAction && (
    <p className="last-action" aria-live="polite">
      <span className="hint">{t('table.lastAction')}: </span>
      {describeEvent(lastAction, seatName, fmt)}
    </p>
  );

  return (
    <div
      className={`table-page ${panelOpen ? '' : 'no-panel'} ${immersive ? 'immersive' : ''} ${compactUi ? 'compact-ui' : ''} ${compactUi && d.myTurn && !maintenance ? 'has-abar' : ''}`}
      style={{ ['--side-w' as string]: `${sideWidth}px` }}
    >
      <div className="table-stage">
        <div className="table-top">
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            {immersive && <div className="immersive-title">{d.tournament.name}</div>}
            <GameInfoBar data={d} last={lastAction ? describeEvent(lastAction, seatName, fmt) : null} />
            {statusPill}
          </div>
          <div className="table-tools">
            {SHOW_FAIRNESS && d.fairness && <FairBadge tournamentId={d.tournament.id} compact={compactUi || immersive} />}
            {immersive && <UnitToggle unit={unit} onChange={(u) => void setUnit(u)} />}
            <button
              type="button"
              className="icon-btn"
              aria-pressed={panelOpen}
              aria-label={panelOpen ? t('table.hidePanel') : chatAvailable ? t('table.showChat') : t('table.showPanel')}
              title={panelOpen ? t('table.hidePanel') : chatAvailable ? t('table.showChat') : t('table.showPanel')}
              onClick={() => setPanelOpen(!panelOpen)}
            >
              <Icon name="panel" />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-pressed={immersive}
              aria-label={immersive ? t('table.exitFullscreen') : t('table.fullscreen')}
              title={immersive ? `${t('table.exitFullscreen')} (${t('table.exitFullscreenHint')})` : t('table.fullscreen')}
              onClick={() => setImmersive(!immersive)}
            >
              <Icon name={immersive ? 'collapse' : 'expand'} />
            </button>
          </div>
        </div>

        {maintenance && d.tournament.status === 'running' && (
          <div className="alert alert-warn" role="status">
            {t('tournament.maintenanceBanner')}
          </div>
        )}
        {d.tournament.status === 'paused' && (
          <div className="alert alert-warn" role="status">
            {t('tournament.pausedBanner', { reason: d.tournament.pauseReason ?? '' })}
          </div>
        )}
        {d.tournament.status === 'finished' && finishRevealed && (
          <div className="alert alert-ok">
            {t('table.finished')} <Link to={`/turnaukset/${d.tournament.id}/tulokset`}>{t('table.showResults')}</Link>
          </div>
        )}
        {d.me?.away && d.me.entryStatus === 'playing' && (
          <div className="alert alert-warn" role="alert">
            {t('table.awayBanner')}{' '}
            <button className="btn btn-brass btn-small" disabled={back.isPending} onClick={() => back.mutate()}>
              {t('table.comeBack')}
            </button>
          </div>
        )}
        {showSince && summary && (
          <section className="since" aria-labelledby="since-h">
            <h2 id="since-h" style={{ fontSize: '1.05rem' }}>
              {t('table.sinceTitle')}
            </h2>
            <ul>
              {summary.handsCompleted > 0 && <li>{t('table.sinceHands', { n: summary.handsCompleted })}</li>}
              {summary.handsCompleted > 0 && (
                <li>
                  {t('table.sinceChips', { delta: `${summary.chipDelta > 0 ? '+' : ''}${fmt(summary.chipDelta)}` })}
                </li>
              )}
              {summary.myTimeouts > 0 && <li>{t('table.sinceTimeouts', { n: summary.myTimeouts })}</li>}
              {summary.biggestPots[0] && summary.biggestPots[0].pot > 0 && (
                <li>
                  {t('table.sinceBig', { n: summary.biggestPots[0].handNo, amount: fmt(summary.biggestPots[0].pot) })} ({summary.biggestPots[0].winners.map(nameOf).join(', ')})
                </li>
              )}
              {summary.events
                .filter((e) => !['joined', 'published'].includes(e.kind))
                .slice(-6)
                .map((e, i) => (
                  <li key={i}>{describeTournamentEvent(e, nameOf)}</li>
                ))}
            </ul>
            <button
              className="btn btn-small"
              onClick={() => {
                setSinceDismissed(true);
                seen.mutate();
                // Drop the cached summary so it does not come back when the tab is reopened.
                qc.removeQueries({ queryKey: ['since', id] });
              }}
            >
              {t('table.sinceOk')}
            </button>
          </section>
        )}

        <div className="felt-area">
        {/* Oval table: wide on desktop and in landscape, upright on phones (positions for both
            are given as CSS variables; a container query picks the right set). */}
        <div className="felt-wrap">
          <div className="felt" />
          <div className="felt-center">
            {potBlock}
            {!hand && d.fairness?.status === 'revealing' && <RevealWaiting tournamentId={d.tournament.id} fairness={d.fairness} />}
          </div>
          {ordered.map((m, i) => {
            const angle = Math.PI / 2 + (i * 2 * Math.PI) / ordered.length;
            const pos = (rx: number, ry: number) => [`${50 + rx * Math.cos(angle)}%`, `${50 + ry * Math.sin(angle)}%`];
            const [x, y] = pos(44, 41);
            const [bx, by] = pos(29, 21);
            const [px, py] = pos(38, 41);
            const [pbx, pby] = pos(24, 27);
            return (
              <div key={m.seat.seat}>
                <div
                  className={`seat ${m.acting ? 'acting' : ''} ${m.inHand?.folded ? 'folded' : ''} ${m.seat.status === 'eliminated' && !show.active ? 'out' : ''} ${m.isMe ? 'me' : ''} ${show.winners?.has(m.seat.seat) ? 'winner' : ''}`}
                  style={{ ['--x' as string]: x, ['--y' as string]: y, ['--px' as string]: px, ['--py' as string]: py }}
                  data-testid={`seat-${m.seat.user.displayName}`}
                >
                  {!m.isMe && <SeatCards m={m} size="s" best={show.bestCards} />}
                  {m.isMe && m.inHand?.hole && (
                    <span className="cards me-cards" role="group" aria-label={t('table.yourHand')}>
                      {m.inHand.hole.map((c) => (
                        <PlayingCard key={c} card={c} size="m" dim={m.inHand!.folded || (!!show.bestCards && !show.bestCards.has(c))} highlight={!!show.bestCards?.has(c)} />
                      ))}
                    </span>
                  )}
                  <div className="seat-plate">
                    <DeadlineRing user={m.seat.user} fraction={turnFraction} active={m.acting} />
                    <AnimatePresence>
                      {show.equity?.has(m.seat.seat) && (
                        <motion.span
                          key="equity"
                          className={`equity-badge ${show.equity.get(m.seat.seat)! >= Math.max(...show.equity.values()) ? 'leading' : ''}`}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          aria-label={t('table.equity', { p: equityText(show.equity.get(m.seat.seat)!) })}
                        >
                          {equityText(show.equity.get(m.seat.seat)!)}
                        </motion.span>
                      )}
                    </AnimatePresence>

                    <div className="seat-info">
                      <span className="seat-name">{m.seat.user.displayName}</span>
                      <span className="seat-stack">{fmt(m.inHand ? m.inHand.stack : m.seat.stack)}</span>
                    </div>
                  </div>
                  <SeatTags m={m} />
                  <AnimatePresence>
                    {show.winners?.get(m.seat.seat) && (
                      <motion.span
                        className="win-badge"
                        initial={{ opacity: 0, y: 6, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                      >
                        +{fmt(show.winners.get(m.seat.seat)!.amount)}
                        {show.winners.get(m.seat.seat)!.category && (
                          <small>{t(`hands.${show.winners.get(m.seat.seat)!.category as 'flush'}`)}</small>
                        )}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <AnimatePresence>
                {m.inHand && m.inHand.streetBet > 0 && (
                  <motion.span
                    key={`bet-${m.seat.seat}-${m.inHand.streetBet}`}
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 420, damping: 26 }}
                    className="seat-bet"
                    style={{ ['--x' as string]: bx, ['--y' as string]: by, ['--px' as string]: pbx, ['--py' as string]: pby, x: '-50%', y: '-50%' }} aria-label={`${t('table.betLabel')} ${m.seat.user.displayName} ${fmt(m.inHand.streetBet)}`}>
                    {fmt(m.inHand.streetBet)}
                  </motion.span>
                )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        </div>

        {/* Own hand + actions. Always in the same place so it stays reachable with chat open. */}
        {show.active && (
          <div className="showdown-bar" role="status">
            <span>{t('table.handEnded', { n: show.handNo ?? '' })}</span>
            <button type="button" className="btn btn-small" onClick={show.skip}>
              {t('table.continue')}
            </button>
          </div>
        )}
        {!show.active && devTools && d.turn && !d.myTurn && d.tournament.status === 'running' && (
          <DevActPanel tournamentId={d.tournament.id} turnKey={`${d.turn.userId}:${d.turn.deadlineAt}:${hand?.handNo}:${hand?.log.length}`} fmt={fmt} />
        )}
        {!show.active && compactUi && canPreSelect && (
          <div className="abar-wrap">
            <PreActions data={d} fmt={fmt} />
          </div>
        )}
        {!show.active && compactUi && d.myTurn && d.hand && !maintenance && (
          <div className="abar-wrap">
            <ActionDock key={d.myTurn.turnId} variant="bar" data={d} unit={unit} fmt={fmt} onActed={() => void qc.invalidateQueries({ queryKey: ['table', id] })} />
          </div>
        )}
        {!show.active && (d.myTurn || myPlayer || d.turn) &&
          !(compactUi && (d.myTurn || immersive || (d.me !== null && d.me.entryStatus !== 'playing'))) && (
          <section className={`action-dock ${compactUi ? 'dock-compact' : ''}`} aria-labelledby="dock-h">
            <div className="dock-head">
              {myPlayer?.hole && (
                <div className="my-cards" role="group" aria-label={t('table.yourHand')}>
                  {myPlayer.hole.map((c, i) => (
                    <motion.span
                      key={`${hand?.handNo}-${c}`}
                      className="card-motion"
                      initial={{ y: -24, rotate: -6, opacity: 0 }}
                      animate={{ y: 0, rotate: 0, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 22, delay: i * 0.1 }}
                    >
                      <PlayingCard card={c} size="l" dim={myPlayer.folded} />
                    </motion.span>
                  ))}
                </div>
              )}
              <div>
                <h2 id="dock-h" className="dock-title" style={{ margin: 0 }}>
                  {d.myTurn ? t('table.yourTurn') : actingName ? t('table.waitingFor', { name: actingName }) : t('table.title')}
                </h2>
                {myPlayer && (
                  <span className="hint num">
                    {t('table.stackLabel')} {fmt(myPlayer.stack)}
                    {myPlayer.streetBet > 0 ? `, ${t('table.betLabel').toLowerCase()} ${fmt(myPlayer.streetBet)}` : ''}
                  </span>
                )}
              </div>
              {deadlineBlock}
            </div>
            {d.myTurn && hand && maintenance ? (
              <p className="hint">{t('tournament.maintenanceWait')}</p>
            ) : d.myTurn && hand ? (
              <motion.div key={d.myTurn.turnId} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 320, damping: 30 }}>
              <ActionDock data={d} unit={unit} fmt={fmt} onActed={() => void qc.invalidateQueries({ queryKey: ['table', id] })} />
              </motion.div>
            ) : d.me === null ? (
              <p className="hint">{t('table.spectating')}</p>
            ) : d.me.entryStatus === 'playing' ? (
              <>
                {canPreSelect && <PreActions data={d} fmt={fmt} />}
                <TurnNotifyControl lead={t('table.notYourTurn')} readOnly turnSeconds={d.tournament.turnSeconds} />
              </>
            ) : null}
          </section>
        )}
      </div>

      <AnimatePresence initial={false}>
      {panelOpen && (
      <motion.aside
        key="side"
        className="side"
        aria-label={t('table.panelLabel')}
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 24, transition: { duration: 0.15 } }}
        transition={{ type: 'spring', stiffness: 360, damping: 34 }}
      >
        <PanelResizer width={sideWidth} onChange={setSideWidth} />
        <div className="side-head">
          <TabBar
            group="table-side"
            label={t('table.panelLabel')}
            active={activeSide}
            items={[
              ...(chatAvailable ? [{ key: 'chat', label: t('table.chatOpen'), onSelect: () => setSide('chat') }] : []),
              { key: 'log', label: t('table.events'), onSelect: () => setSide('log') },
            ]}
          />
          {/* Over a fullscreen phone table the panel covers the toolbar, so it closes from here. */}
          {overlay && (
            <button type="button" className="icon-btn side-close" aria-label={t('table.hidePanel')} title={t('table.hidePanel')} onClick={() => setPanelOpen(false)}>
              <Icon name="close" />
            </button>
          )}
        </div>
        <div className="side-body">
          {activeSide === 'chat' && channel ? (
            <ChannelView channelId={channel} compact />
          ) : (
            <HandLog
              hand={hand}
              // The last hand's summary and its eliminations name the winner: they wait for the
              // winner's screen like the rest of the ending.
              previous={spoiler ? null : d.previousHand}
              events={spoiler ? d.events.filter((e) => e.kind !== 'finished' && (e.handNo === null || e.handNo !== d.previousHand?.handNo)) : d.events}
              seatName={seatName}
              nameOf={nameOf}
              fmt={fmt}
              unit={unit}
              tournamentId={d.fairness ? d.tournament.id : undefined}
            />
          )}
        </div>
      </motion.aside>
      )}
      </AnimatePresence>
    </div>
  );
}

function useChannelId(tournamentId: string, enabled: boolean): string | null {
  const q = useQuery({
    queryKey: ['tournament', tournamentId],
    queryFn: () => api.get<{ channelId: string | null }>(`/api/tournaments/${tournamentId}`),
    enabled,
  });
  return q.data?.channelId ?? null;
}

function HandLog({
  hand,
  previous,
  events,
  seatName,
  nameOf,
  fmt,
  unit,
  tournamentId,
}: {
  tournamentId?: string;
  hand: HandView | null;
  previous: HandSummary | null;
  events: TableView['events'];
  seatName: (seat: number) => string;
  nameOf: (id: string) => string;
  fmt: AmountFormatter;
  unit: AmountUnit;
}) {
  return (
    <div>
      {hand && (
        <>
          <h3 className="log-title">{t('table.handNo', { n: hand.handNo })}</h3>
          <HandEventList log={hand.log} name={seatName} fmt={fmt} keyPrefix={`${hand.handNo}`} />
        </>
      )}
      {previous && <PreviousHand summary={previous} nameOf={nameOf} unit={unit} tournamentId={tournamentId} />}
      <h3 className="log-title">{t('table.events')}</h3>
      <ol className="tevents">
        {events
          .filter((e) => !['joined', 'published'].includes(e.kind))
          .map((e) => (
            <li key={e.id} className={`tevent tevent-${e.kind}`}>
              <span className="tevent-icon" aria-hidden="true">
                {TEVENT_ICON[e.kind] ?? '•'}
              </span>
              <span className="tevent-text">{describeTournamentEvent(e, nameOf)}</span>
              <EventTime at={e.createdAt} />
            </li>
          ))}
      </ol>
    </div>
  );
}

/** Winners of the previous hand, one line per player even when they won several pots. */
function mergedWinners(summary: HandSummary) {
  const byUser = new Map<string, { userId: string; amount: number; pots: number; category: string | null }>();
  for (const p of summary.pots) {
    for (const w of p.winners) {
      const cur = byUser.get(w.userId);
      if (cur) {
        cur.amount += w.amount;
        cur.pots += 1;
      } else byUser.set(w.userId, { userId: w.userId, amount: w.amount, pots: 1, category: p.category });
    }
  }
  return [...byUser.values()];
}

export function PreviousHand({
  summary,
  nameOf,
  unit = 'chips',
  tournamentId,
}: {
  summary: HandSummary;
  nameOf: (id: string) => string;
  unit?: AmountUnit;
  tournamentId?: string;
}) {
  const fmt = makeFormatter(unit, summary.level.bb);
  return (
    <div style={{ padding: '4px 14px 8px' }}>
      <h3 style={{ margin: '8px 0 4px' }}>
        {t('table.previousHand', { n: summary.handNo })}{' '}
        {tournamentId && (
          <Link className="verify-link" to={`/turnaukset/${tournamentId}/jaot/${summary.handNo}#tarkista`}>
            {t('fair.verify')}
          </Link>
        )}
      </h3>
      {summary.board.length > 0 && (
        <div className="cards cards-tight" style={{ margin: '4px 0' }}>
          {summary.board.map((c) => (
            <PlayingCard key={c} card={c} size="s" />
          ))}
        </div>
      )}
      <ul className="log" style={{ padding: 0 }}>
        {mergedWinners(summary).map((w) => (
          <li key={w.userId}>
            {t('table.wonPot', { name: nameOf(w.userId), amount: fmt(w.amount) })}
            {w.pots > 1 ? ` (${t('log.potsWon', { n: w.pots })})` : ''}{' '}
            {w.category ? t('table.withHand', { hand: t(`hands.${w.category as 'flush'}`) }) : ''}
          </li>
        ))}
        {(summary.autoActions ?? []).map((a, i) => (
          <li key={`auto-${i}`} className="auto">
            {t(`log.${a.action as 'fold'}`, { name: nameOf(a.userId), amount: '' }).trim()}
            {a.reason === 'timeout' ? t('log.autoTimeout') : t('log.autoAway')}
          </li>
        ))}
        {summary.players
          .filter((p) => p.shown)
          .map((p) => (
            <li key={p.userId}>
              {nameOf(p.userId)}: <InlineCards cards={p.shown!} />
            </li>
          ))}
      </ul>
    </div>
  );
}


/** A boolean preference kept in this browser (e.g. whether the side panel is open). */
function usePersistentFlag(key: string, initial: boolean): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : raw === '1';
    } catch {
      return initial;
    }
  });
  const set = (v: boolean) => {
    setValue(v);
    try {
      localStorage.setItem(key, v ? '1' : '0');
    } catch {
      // storage unavailable (private mode); the choice lasts for this visit only
    }
  };
  return [value, set];
}

/**
 * Full-screen table. Uses the Fullscreen API where available; everywhere (including iPhone
 * Safari, which does not support it for pages) the table also covers the whole window and the
 * app navigation is hidden. Esc or the button leaves the mode.
 */
/**
 * "Check/Fold", "Check" or "Call X" chosen before one's own turn. Stored on the server and
 * applied the moment the turn comes, if it still fits (a raise or a new round clears it).
 */
function PreActions({ data, fmt }: { data: TableView; fmt: AmountFormatter }) {
  const qc = useQueryClient();
  const toast = useToast();
  const hand = data.hand!;
  const me = hand.players.find((p) => p.seat === data.me?.seat)!;
  const toCall = Math.max(0, Math.min(hand.currentBet - me.streetBet, me.stack));
  const current = data.myPreAction;
  const set = useMutation({
    mutationFn: (kind: 'check_fold' | 'check' | 'call' | null) =>
      api.put(`/api/tournaments/${data.tournament.id}/pre-action`, { handNo: hand.handNo, kind, ...(kind === 'call' && { callAmount: toCall }) }),
    onError: (err) => toast(errorText(err), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['table', data.tournament.id] }),
  });
  const options: { kind: 'check_fold' | 'check' | 'call'; label: string }[] = [
    { kind: 'check_fold', label: toCall > 0 ? t('table.preFold') : t('table.preCheckFold') },
    toCall > 0 ? { kind: 'call', label: `${t('table.preCall')} ${fmt(toCall)}` } : { kind: 'check', label: t('table.preCheck') },
  ];
  return (
    <div className="pre-actions" role="group" aria-label={t('table.preTitle')}>
      <span className="pre-title">{t('table.preTitle')}</span>
      <div className="pre-options">
        {options.map((o) => {
          const on = current?.kind === o.kind && (o.kind !== 'call' || current.callAmount === toCall);
          return (
            <button key={o.kind} type="button" className={`pre-option ${on ? 'on' : ''}`} aria-pressed={on} disabled={set.isPending} onClick={() => set.mutate(on ? null : o.kind)}>
              <span className="pre-box" aria-hidden="true">
                {on ? '✓' : ''}
              </span>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

type LockableOrientation = ScreenOrientation & { lock?: (o: string) => Promise<void>; unlock?: () => void };

async function lockLandscape(): Promise<void> {
  try {
    await (screen.orientation as LockableOrientation | undefined)?.lock?.('landscape');
  } catch {
    // not supported: the CSS fallback rotates the view instead
  }
}

function unlockOrientation(): void {
  try {
    (screen.orientation as LockableOrientation | undefined)?.unlock?.();
  } catch {
    // ignore
  }
}

function useImmersive(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(false);
  const set = (next: boolean) => {
    setOn(next);
    if (next) {
      if (document.fullscreenEnabled && !document.fullscreenElement) {
        document.documentElement
          .requestFullscreen({ navigationUI: 'hide' })
          // Phones turn to landscape where the browser allows it (Android). Elsewhere, e.g. on
          // iPhone, CSS rotates the view while the phone is held upright.
          .then(() => (window.matchMedia('(max-width: 760px), (max-height: 520px)').matches ? lockLandscape() : undefined))
          .catch(() => undefined);
      }
    } else {
      unlockOrientation();
      if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    }
  };
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setOn(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) setOn(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  useEffect(() => {
    document.body.classList.toggle('table-immersive', on);
    return () => document.body.classList.remove('table-immersive');
  }, [on]);
  useEffect(
    () => () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    },
    [],
  );
  return [on, set];
}


/** Current game state at a glance: hand, street progress, blinds and progress to the next level. */
function GameInfoBar({ data, last }: { data: TableView; last: ReactNode }) {
  const hand = data.hand;
  const level = data.tournament.level;
  const next = data.tournament.nextLevel;
  const perLevel = data.tournament.handsPerLevel;
  // Hands played at this level including the current one (1-based); "inHands" counts the rest.
  const handInLevel = next ? Math.min(perLevel, Math.max(1, perLevel - next.inHands)) : null;
  return (
    <dl className="info-bar num">
      <div className="info-cell">
        <dt>{t('table.infoHand')}</dt>
        <dd>{hand ? `#${hand.handNo}` : '–'}</dd>
      </div>
      <div className="info-cell">
        <dt>{t('table.infoBlinds', { n: level.index + 1 })}</dt>
        <dd>
          {chips(level.sb)}/{chips(level.bb)}
          {level.ante > 0 && <span className="info-sub"> {t('table.infoAnte', { ante: chips(level.ante) })}</span>}
        </dd>
      </div>
      <div className="info-cell info-next">
        <dt>{next ? t('table.infoNext') : t('table.lastLevel')}</dt>
        <dd>
          {next ? (
            <>
              <span>
                {chips(next.sb)}/{chips(next.bb)}
                <span className="info-sub"> {next.inHands === 0 ? t('table.infoNextNow') : t('table.infoNextIn', { n: next.inHands })}</span>
              </span>
              <span
                className="level-progress"
                role="progressbar"
                aria-label={t('table.infoLevelProgress')}
                aria-valuemin={0}
                aria-valuemax={perLevel}
                aria-valuenow={handInLevel ?? 0}
              >
                <motion.span
                  className="level-progress-fill"
                  initial={false}
                  animate={{ width: `${((handInLevel ?? 0) / perLevel) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 160, damping: 26 }}
                />
              </span>
            </>
          ) : (
            '–'
          )}
        </dd>
      </div>
      <div className="info-cell info-last" aria-live="polite">
        <dt>{t('table.lastAction')}</dt>
        <dd>{last ?? '–'}</dd>
      </div>
    </dl>
  );
}

const SIDE_MIN = 280;
const SIDE_MAX = 560;
const SIDE_DEFAULT = 340;

/** Chat panel width on desktop: draggable, clamped, remembered in this browser. */
function usePanelWidth(): [number, (w: number) => void] {
  const [w, setW] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('kotipoker.sideWidth'));
      return v >= SIDE_MIN && v <= SIDE_MAX ? v : SIDE_DEFAULT;
    } catch {
      return SIDE_DEFAULT;
    }
  });
  const set = (next: number) => {
    const clamped = Math.round(Math.max(SIDE_MIN, Math.min(SIDE_MAX, next)));
    setW(clamped);
    try {
      localStorage.setItem('kotipoker.sideWidth', String(clamped));
    } catch {
      // not persisted
    }
  };
  return [w, set];
}

function PanelResizer({ width, onChange }: { width: number; onChange: (w: number) => void }) {
  const start = useRef<{ x: number; w: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div
      className={`panel-resizer ${dragging ? 'dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('table.resizePanel')}
      aria-valuemin={SIDE_MIN}
      aria-valuemax={SIDE_MAX}
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { x: e.clientX, w: width };
        setDragging(true);
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        // The panel is on the right: dragging left makes it wider.
        onChange(start.current.w + (start.current.x - e.clientX));
      }}
      onPointerUp={() => {
        start.current = null;
        setDragging(false);
      }}
      onPointerCancel={() => {
        start.current = null;
        setDragging(false);
      }}
      onDoubleClick={() => onChange(SIDE_DEFAULT)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onChange(width + 20);
        else if (e.key === 'ArrowRight') onChange(width - 20);
        else if (e.key === 'Home') onChange(SIDE_MIN);
        else if (e.key === 'End') onChange(SIDE_MAX);
        else return;
        e.preventDefault();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Visual hand log

const TEVENT_ICON: Record<string, string> = {
  started: '▶',
  level_up: '▲',
  eliminated: '✕',
  away: '⏸',
  back: '↩',
  paused: '⏸',
  resumed: '▶',
  deadline_extended: '⏱',
  cancelled: '⛔',
  finished: '🏆',
  corrected: '✎',
  terms_changed: '✎',
};

function nameHue(name: string): number {
  return [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 17);
}

function Who({ name }: { name: string }) {
  return (
    <span className="log-who">
      <span className="log-dot" style={{ ['--hue' as string]: String(nameHue(name)) }} aria-hidden="true" />
      {name}
    </span>
  );
}

const ACTION_LABEL: Record<string, string> = { fold: 'Fold', check: 'Check', call: 'Call', bet: 'Bet', raise: 'Raise' };

function EventTime({ at }: { at: number | undefined }) {
  if (!at) return null;
  return (
    <time className="log-time" dateTime={new Date(at).toISOString()} title={dateTime(at)}>
      {smartTime(at)}
    </time>
  );
}

/** A visible pause between events: async turns can be hours apart. */
const GAP_MS = 20 * 60_000;

function HandEventItem({ e, name, fmt }: { e: HandEvent; name: (seat: number) => string; fmt: AmountFormatter }) {
  switch (e.t) {
    case 'street':
      return (
        <li className="log-street">
          <span className="log-street-name">{t(`table.streets.${e.street}`)}</span>
          <span className="cards cards-tight">
            {e.cards.map((c) => (
              <PlayingCard key={c} card={c} size="s" />
            ))}
          </span>
          <EventTime at={e.at} />
        </li>
      );
    case 'action': {
      const kind = e.allIn && e.action !== 'fold' && e.action !== 'check' ? 'allin' : e.action;
      const amount = e.action === 'call' ? e.added : e.action === 'bet' || e.action === 'raise' ? e.to : 0;
      return (
        <li className={`log-action ${e.auto ? 'is-auto' : ''}`}>
          <Who name={name(e.seat)} />
          <span className={`act-badge ${kind}`}>{kind === 'allin' ? 'All-in' : ACTION_LABEL[e.action]}</span>
          {amount > 0 && <span className="log-amount num">{fmt(amount)}</span>}
          {e.auto && (
            <span className="log-auto" title={e.auto === 'timeout' ? t('log.autoTimeoutLong') : t('log.autoAwayLong')}>
              ⏱ {e.auto === 'timeout' ? t('log.autoTimeoutShort') : t('log.autoAwayShort')}
            </span>
          )}
          <EventTime at={e.at} />
        </li>
      );
    }
    case 'blind':
    case 'ante':
      return (
        <li className="log-minor">
          {name(e.seat)} <span className="minor-tag">{e.t === 'ante' ? 'Ante' : e.kind === 'sb' ? 'SB' : 'BB'}</span>{' '}
          <span className="num">{fmt(e.amount)}</span>
          {e.allIn ? ` (${t('table.allIn')})` : ''}
        </li>
      );
    case 'return':
      return <li className="log-minor">{t('log.return', { name: name(e.seat), amount: fmt(e.amount) })}</li>;
    case 'reveal':
      return (
        <li className="log-reveal">
          <Who name={name(e.seat)} />
          <span className="log-shows">{t('log.shows')}</span>
          <span className="cards cards-tight">
            {e.cards.map((c) => (
              <PlayingCard key={c} card={c} size="s" />
            ))}
          </span>
          <EventTime at={e.at} />
        </li>
      );
    case 'muck':
      return <li className="log-minor">{t('log.muck', { name: name(e.seat) })}</li>;
    case 'award': {
      // Several pots won by the same player are shown as one line (see mergeAwards).
      const pots = (e as HandEvent & { pots?: number }).pots ?? 1;
      return (
        <li className="log-award">
          <span aria-hidden="true">🏆</span>
          <Who name={name(e.seat)} />
          <span>{t('log.wins')}</span>
          <strong className="num">{fmt(e.amount)}</strong>
          {pots > 1 && <span className="log-pots">{t('log.potsWon', { n: pots })}</span>}
          <EventTime at={e.at} />
        </li>
      );
    }
    default:
      return null;
  }
}

/** Main pot and side pots won by the same player become one line with the total. */
function mergeAwards(log: HandEvent[]): HandEvent[] {
  const out: HandEvent[] = [];
  for (const e of log) {
    if (e.t === 'award') {
      const prev = out.find((x) => x.t === 'award' && x.seat === e.seat) as (HandEvent & { t: 'award'; pots?: number }) | undefined;
      if (prev) {
        prev.amount += e.amount;
        prev.pots = (prev.pots ?? 1) + 1;
        continue;
      }
      out.push({ ...e });
      continue;
    }
    out.push(e);
  }
  return out;
}

export function HandEventList({ log, name, fmt, keyPrefix }: { log: HandEvent[]; name: (seat: number) => string; fmt: AmountFormatter; keyPrefix: string }) {
  let prevAt: number | undefined;
  return (
    <ol className="hlog" aria-live="polite">
      {mergeAwards(log).map((e, i) => {
        if (e.t === 'deal') return null;
        const gap = e.at !== undefined && prevAt !== undefined ? e.at - prevAt : 0;
        if (e.at !== undefined) prevAt = e.at;
        return (
          <Fragment key={`${keyPrefix}-${i}`}>
            {gap >= GAP_MS && (
              <li className="log-gap" aria-hidden="true">
                <span>{t('time.later', { d: duration(gap) })}</span>
              </li>
            )}
            <HandEventItem e={e} name={name} fmt={fmt} />
          </Fragment>
        );
      })}
    </ol>
  );
}
